import React, { useState } from "react";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { CopySimpleIcon as CopySimple, CheckCircleIcon as CheckCircle, WarningIcon as Warning, ArrowSquareOutIcon as ArrowSquareOut, ShieldCheckIcon as ShieldCheck, CurrencyDollarIcon as CurrencyDollar, LightningIcon as Lightning, PlugIcon as Plug, QuestionIcon as Question } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Admin / Checkr setup & payment flow reference.
 *
 * Lives at /admin/checkr-setup. A self-contained, copy-pasteable
 * playbook for going from MOCKED Checkr (today) → LIVE Checkr.
 *
 * Why a dedicated page: founder asked for "exact instructions" they
 * can find later. Stuffing this into a README would lose it. Wiring
 * it into the admin console means it's two clicks from anywhere.
 *
 * Currently in the codebase:
 *   - $39 Stripe SKU `service_provider_bg_check` is LIVE (real money flows).
 *   - On webhook success, provider's `background_check.status` flips
 *     to `requested` with `checkr_candidate_id: None` (see
 *     /app/backend/routers/billing/fulfill.py:633-656).
 *   - Admin manually advances status via
 *     PUT /api/admin/providers/{id}/background-check (stubbed).
 *
 * What's NOT live yet:
 *   - The actual Checkr API call (create candidate, invite, poll
 *     report, store checkr_candidate_id).
 *   - The webhook handler for Checkr report status changes.
 */

const CODE_BLOCKS = {
  envKeys: `# /app/backend/.env  — add these:
CHECKR_API_KEY=test_xxxxxxxxxxxxxxxxxxxxxxxxxxxx
CHECKR_WEBHOOK_SECRET=whsec_xxxxxxxxxxxxxxxxxxxxxxxx
CHECKR_PACKAGE_SLUG=tasker_standard`,

  pythonCreate: `# /app/backend/services/checkr.py  — new file
import os
import httpx

CHECKR_BASE = "https://api.checkr.com/v1"
CHECKR_API_KEY = os.environ["CHECKR_API_KEY"]
CHECKR_PACKAGE = os.environ.get("CHECKR_PACKAGE_SLUG", "tasker_standard")

async def create_candidate(provider: dict) -> dict:
    """Create a Checkr candidate. Returns {id, invitation_url}."""
    payload = {
        "first_name": provider["first_name"],
        "last_name":  provider["last_name"],
        "email":      provider["email"],
        "no_middle_name": True,
        "work_locations": [{"country": "US", "state": provider["state"]}],
    }
    async with httpx.AsyncClient() as c:
        r = await c.post(
            f"{CHECKR_BASE}/candidates",
            auth=(CHECKR_API_KEY, ""),
            json=payload,
            timeout=15.0,
        )
        r.raise_for_status()
        cand = r.json()
        # Send the invitation — candidate completes via Checkr's hosted flow.
        inv = await c.post(
            f"{CHECKR_BASE}/invitations",
            auth=(CHECKR_API_KEY, ""),
            json={"candidate_id": cand["id"], "package": CHECKR_PACKAGE},
            timeout=15.0,
        )
        inv.raise_for_status()
        return {"id": cand["id"], "invitation_url": inv.json()["invitation_url"]}`,

  webhookHandler: `# /app/backend/routers/checkr_webhook.py  — new file
import hmac, hashlib, os, json
from fastapi import APIRouter, Request, HTTPException
from db import db

router = APIRouter(prefix="/checkr", tags=["webhooks"])
SECRET = os.environ["CHECKR_WEBHOOK_SECRET"]

def _verify(body: bytes, sig: str) -> bool:
    digest = hmac.new(SECRET.encode(), body, hashlib.sha256).hexdigest()
    return hmac.compare_digest(digest, sig)

@router.post("/webhook")
async def checkr_webhook(req: Request):
    body = await req.body()
    sig  = req.headers.get("X-Checkr-Signature", "")
    if not _verify(body, sig):
        raise HTTPException(401, "Bad signature")
    event = json.loads(body)
    if event["type"] == "report.completed":
        report   = event["data"]["object"]
        cand_id  = report["candidate_id"]
        decision = "clear" if report["status"] == "clear" else "fail"
        await db.service_providers.update_one(
            {"background_check.checkr_candidate_id": cand_id},
            {"$set": {
                "background_check.status": decision,
                "background_check.completed_at": report["completed_at"],
            }},
        )
    return {"ok": True}`,

  wireUp: `# /app/backend/routers/billing/fulfill.py  — replace the stubbed
# branch at line ~933 (kind == "service_provider_bg_check") with:

from services.checkr import create_candidate

if kind == "service_provider_bg_check":
    prov = await db.service_providers.find_one(
        {"user_id": user["id"]},
        {"_id": 0, "first_name": 1, "last_name": 1, "email": 1, "state": 1},
    )
    cand = await create_candidate(prov)
    await db.service_providers.update_one(
        {"user_id": user["id"]},
        {"$set": {
            "background_check.status": "requested",
            "background_check.checkr_candidate_id": cand["id"],
            "background_check.invitation_url":     cand["invitation_url"],
            "background_check.paid": True,
            "background_check.paid_amount": 39.00,
            "background_check.requested_at": now.isoformat(),
        }},
    )
    # Email the provider with the invitation link.`,
};

function CodeBlock({ id, body }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(body);
      setCopied(true);
      toast.success("Copied");
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.error("Clipboard blocked");
    }
  };
  return (
    <div className="relative" data-testid={`checkr-code-${id}`}>
      <Button
        size="sm"
        variant="outline"
        onClick={handleCopy}
        className="absolute top-3 right-3 rounded-full border-white/20 bg-ink/60 backdrop-blur text-white hover:border-gold hover:text-gold h-7 px-3 text-[11px]"
        data-testid={`checkr-copy-${id}`}
      >
        {copied ? <CheckCircle size={12} weight="fill" /> : <CopySimple size={12} />}
        <span className="ml-1">{copied ? "Copied" : "Copy"}</span>
      </Button>
      <pre className="bg-ink text-white text-[12.5px] leading-relaxed rounded-lg p-5 pr-24 overflow-x-auto font-mono border border-gold/20">
{body}
      </pre>
    </div>
  );
}

function Step({ num, title, children, testid }) {
  return (
    <div className="relative pl-12 pb-10" data-testid={testid}>
      <div className="absolute left-0 top-0 w-9 h-9 rounded-full bg-gold-gradient text-ink font-bold flex items-center justify-center text-[15px] shadow-[0_4px_12px_rgba(201,162,39,0.35)]">
        {num}
      </div>
      <h3 className="font-spectral text-2xl text-ink mb-3 leading-tight">{title}</h3>
      <div className="text-[14.5px] text-[#3F3F3F] leading-relaxed space-y-3">{children}</div>
    </div>
  );
}

export default function AdminCheckrSetup() {
  return (
    <AppShell>
      <div className="max-w-4xl mx-auto px-6 md:px-10 py-10" data-testid="admin-checkr-setup-page">
        {/* Header */}
        <div className="mb-12">
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-crimson mb-2">
            Admin · Operations
          </p>
          <h1 className="font-spectral text-4xl md:text-5xl text-ink leading-tight">
            Checkr — setup & payment flow
          </h1>
          <p className="mt-3 text-[15px] text-[#3F3F3F] max-w-2xl">
            Today the $39 background-check fee charges real money but the
            screening itself is admin-advanced. This is the exact playbook to
            wire the live Checkr API in when you're ready.
          </p>
          <div className="mt-5 inline-flex items-center gap-2 rounded-full bg-amber-50 border border-amber-200 px-3.5 py-1.5 text-[12px] text-amber-900" data-testid="checkr-status-banner">
            <Warning size={13} weight="fill" />
            <span><strong>Status: MOCKED.</strong> Real Stripe payment, admin-advanced status. No API call to Checkr yet.</span>
          </div>
        </div>

        {/* Payment flow first — what happens TODAY when a provider pays */}
        <section className="mb-14" data-testid="checkr-payment-flow">
          <div className="flex items-center gap-2 mb-5">
            <CurrencyDollar size={20} weight="duotone" className="text-gold" />
            <h2 className="font-spectral text-3xl text-ink">How the $39 payment works (today)</h2>
          </div>
          <div className="bg-cream/40 border border-[#E2DAC2] rounded-xl p-6 space-y-4 text-[14.5px] text-[#3F3F3F]">
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <p className="text-[10px] uppercase tracking-[0.22em] text-gold-dark font-bold mb-1.5">A. Provider pays</p>
                <p>Walker / sitter clicks <em>"Start background check"</em> → POST <code className="text-[12px] bg-white px-1 py-0.5 rounded">/api/providers/mine/background-check/start</code> returns Stripe SKU <code className="text-[12px] bg-white px-1 py-0.5 rounded">service_provider_bg_check</code> → standard <code className="text-[12px] bg-white px-1 py-0.5 rounded">/api/billing/checkout</code> flow → <strong>Stripe Checkout: $39</strong>.</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.22em] text-gold-dark font-bold mb-1.5">B. Webhook fires</p>
                <p>Stripe webhook → <code className="text-[12px] bg-white px-1 py-0.5 rounded">routers/billing/fulfill.py:933</code> flips the provider's <code className="text-[12px] bg-white px-1 py-0.5 rounded">background_check.status</code> from <code>not_started</code> → <strong><code>requested</code></strong>. <code>checkr_candidate_id</code> is still <code>None</code>.</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.22em] text-gold-dark font-bold mb-1.5">C. Admin advances</p>
                <p>You manually flip status to <code>clear</code> or <code>fail</code> via <strong>Admin → Providers</strong> (or <code className="text-[12px] bg-white px-1 py-0.5 rounded">PUT /api/admin/providers/{`{id}`}/background-check</code>). Live Checkr API will do this for you once wired.</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.22em] text-gold-dark font-bold mb-1.5">D. Provider gets $25 back</p>
                <p>After the provider's FIRST completed booking, a <strong>$25 Stripe Connect transfer</strong> fires to their payout account. Net cost for active providers: <strong>$14</strong>. Tire-kickers who never book pay the full $39 — covers our Checkr cost (~$25-30).</p>
              </div>
            </div>
            <div className="mt-2 pt-4 border-t border-[#E2DAC2] text-[12.5px] text-[#6B6B6B]">
              <strong className="text-ink">Net unit economics:</strong> Revenue $39 · Checkr cost ~$25–30 · Stripe fee ~$1.43 · Credit refund $25 (only if they book) = <strong>net ~$13 loss</strong> per active provider on screening alone — but they bring 12% take-rate on every booking forever. Tire-kickers net <strong>+$8</strong>.
            </div>
          </div>
        </section>

        {/* Setup playbook */}
        <section className="mb-10" data-testid="checkr-setup-steps">
          <div className="flex items-center gap-2 mb-5">
            <Plug size={20} weight="duotone" className="text-gold" />
            <h2 className="font-spectral text-3xl text-ink">Setup — going live with Checkr</h2>
          </div>

          <Step num={1} title="Create your Checkr account" testid="checkr-step-1">
            <p>
              Go to <a href="https://checkr.com/customers/sign_up" target="_blank" rel="noopener noreferrer" className="text-gold-dark underline underline-offset-2 inline-flex items-center gap-1">checkr.com/customers/sign_up <ArrowSquareOut size={12} /></a>.
              Sign up as a business. They'll ask for: legal entity name, EIN,
              business address, primary contact. Approval is usually 1–2
              business days for a marketplace use case.
            </p>
            <p>
              When asked about <strong>"who you'll screen"</strong>, say:
              <em>"Independent service providers (dog walkers, pet sitters)
              listed on a marketplace venue. We pay for the screening; the
              providers consent via Checkr's hosted invitation flow."</em>
              That's the wording that gets a marketplace account approved
              fastest.
            </p>
          </Step>

          <Step num={2} title="Pick your package" testid="checkr-step-2">
            <p>
              Inside the Checkr dashboard, navigate to{" "}
              <strong>Settings → Packages</strong>. For walkers + sitters we
              recommend the <strong>"Tasker Standard"</strong> package —
              SSN trace + national criminal + sex-offender registry. Cost is
              roughly $25–30/screening depending on your volume tier.
            </p>
            <p className="text-[12.5px] text-[#6B6B6B]">
              Slugs you'll need: copy the package slug (looks like{" "}
              <code className="bg-cream px-1 rounded">tasker_standard</code>{" "}
              or <code className="bg-cream px-1 rounded">driver_pro_full</code>).
            </p>
          </Step>

          <Step num={3} title="Grab your API key + Webhook secret" testid="checkr-step-3">
            <p>
              <strong>Settings → API Keys</strong> — issue a <em>Live</em> key
              (or <em>Test</em> for sandbox). Live keys start with{" "}
              <code className="bg-cream px-1 rounded">prod_</code>, test keys
              start with <code className="bg-cream px-1 rounded">test_</code>.
            </p>
            <p>
              <strong>Settings → Webhooks</strong> — add a new endpoint
              pointing at{" "}
              <code className="bg-cream px-1 rounded font-tabular">https://armstrongk9registry.com/api/checkr/webhook</code>.
              Subscribe to: <code>report.completed</code>,{" "}
              <code>report.suspended</code>, <code>candidate.created</code>.
              Copy the signing secret (starts with{" "}
              <code className="bg-cream px-1 rounded">whsec_</code>).
            </p>
          </Step>

          <Step num={4} title="Add env vars to the backend" testid="checkr-step-4">
            <CodeBlock id="env" body={CODE_BLOCKS.envKeys} />
            <p className="text-[12.5px] text-[#6B6B6B]">
              Restart the backend with{" "}
              <code className="bg-cream px-1 rounded">sudo supervisorctl restart backend</code>{" "}
              after adding env vars.
            </p>
          </Step>

          <Step num={5} title="Add the Checkr client service" testid="checkr-step-5">
            <p>Create the file below — it's the thin HTTP wrapper that creates a candidate and sends the invitation:</p>
            <CodeBlock id="client" body={CODE_BLOCKS.pythonCreate} />
          </Step>

          <Step num={6} title="Add the webhook receiver" testid="checkr-step-6">
            <p>Checkr POSTs report status changes here. We verify the HMAC signature and flip the provider's status in MongoDB:</p>
            <CodeBlock id="webhook" body={CODE_BLOCKS.webhookHandler} />
            <p className="text-[12.5px] text-[#6B6B6B]">
              Don't forget to register the router in{" "}
              <code className="bg-cream px-1 rounded">/app/backend/server.py</code>:{" "}
              <code className="bg-cream px-1 rounded">app.include_router(checkr_webhook.router, prefix="/api")</code>.
            </p>
          </Step>

          <Step num={7} title="Wire it into the Stripe fulfillment branch" testid="checkr-step-7">
            <p>
              Replace the stubbed branch in{" "}
              <code className="bg-cream px-1 rounded">fulfill.py</code> (line ~933)
              with the live Checkr call:
            </p>
            <CodeBlock id="wire" body={CODE_BLOCKS.wireUp} />
          </Step>

          <Step num={8} title="Test sandbox → flip to live" testid="checkr-step-8">
            <p>
              Use Checkr's <strong>Test mode</strong> first (test API key +
              test webhook). Their sandbox lets you script outcomes — you can
              POST <code className="bg-cream px-1 rounded">/v1/candidates/{`{id}`}/test_complete?status=clear</code>{" "}
              to fast-forward a candidate to a clear result without waiting
              for real screening.
            </p>
            <p>
              Once you've watched a full <em>$39 Stripe payment → Checkr
              candidate created → email invitation sent → test report clear →
              status=clear in our DB</em> loop, swap the env vars to your{" "}
              <code className="bg-cream px-1 rounded">prod_</code> key and
              you're live.
            </p>
          </Step>
        </section>

        {/* Compliance notes */}
        <section className="mb-12" data-testid="checkr-compliance">
          <div className="flex items-center gap-2 mb-5">
            <ShieldCheck size={20} weight="duotone" className="text-gold" />
            <h2 className="font-spectral text-3xl text-ink">Compliance — don't skip this</h2>
          </div>
          <div className="border border-crimson/30 bg-crimson/5 rounded-xl p-6 text-[14px] text-[#3F3F3F] space-y-3">
            <p>
              <strong className="text-ink">FCRA disclosure + authorization.</strong>{" "}
              Federal law requires you to give the provider a standalone FCRA
              disclosure and get their written authorization BEFORE
              ordering a background check. Checkr's hosted invitation flow
              handles this — DO NOT bypass it by collecting SSNs yourself.
            </p>
            <p>
              <strong className="text-ink">Adverse action workflow.</strong>{" "}
              If a report comes back with a hit and you want to suspend the
              provider, you must follow the two-step adverse action process:
              <br />
              <span className="ml-4 block">1. Send the <em>pre-adverse action notice</em> + a copy of the report (Checkr automates this).</span>
              <span className="ml-4 block">2. Wait 5 business days for the candidate to dispute.</span>
              <span className="ml-4 block">3. Then send the <em>final adverse action notice</em>.</span>
            </p>
            <p>
              <strong className="text-ink">State-specific rules.</strong>{" "}
              California, NYC, and Cook County have ban-the-box rules that
              limit what you can ask about and when. Checkr's package
              configuration handles this automatically if you set the{" "}
              <code className="bg-white px-1 rounded">work_locations</code>{" "}
              field on the candidate.
            </p>
            <p className="pt-2 border-t border-crimson/15 text-[12.5px] text-[#6B6B6B]">
              <strong className="text-ink">Venue posture:</strong> Disclose in
              your provider terms that "Armstrong K9 Registry uses Checkr to
              verify provider background — Armstrong K9 Registry is the
              venue, providers are independent contractors, and a clear
              background check is not a guarantee of conduct." That's already
              in <code className="bg-white px-1 rounded">/app/memory/SERVICE_PROVIDERS_PRD.md</code>{" "}
              §8.
            </p>
          </div>
        </section>

        {/* FAQ */}
        <section className="mb-12" data-testid="checkr-faq">
          <div className="flex items-center gap-2 mb-5">
            <Question size={20} weight="duotone" className="text-gold" />
            <h2 className="font-spectral text-3xl text-ink">FAQ</h2>
          </div>
          <div className="space-y-5 text-[14px] text-[#3F3F3F]">
            <div>
              <p className="font-bold text-ink mb-1">Can I require background checks on trainers + groomers too?</p>
              <p>
                Yes, but it's optional in the current code (see{" "}
                <code className="bg-cream px-1 rounded">_BG_CHECK_REQUIRED_KINDS</code>{" "}
                in <code className="bg-cream px-1 rounded">service_providers.py</code>).
                The default is walkers + sitters only because they have unsupervised access to the dog and home. Add <code>"trainer"</code> + <code>"groomer"</code> to that set if you want to require it across the board.
              </p>
            </div>
            <div>
              <p className="font-bold text-ink mb-1">What if a provider's background check fails after they've already taken bookings?</p>
              <p>
                Status flips to <code>fail</code> → cron at{" "}
                <code className="bg-cream px-1 rounded">services/provider_lifecycle.py</code>{" "}
                auto-suspends them from new bookings. Existing bookings within
                their cancellation window get refunded. The provider is
                emailed adverse-action notice; you get a row in admin
                disputes queue.
              </p>
            </div>
            <div>
              <p className="font-bold text-ink mb-1">How much does Checkr actually cost per check?</p>
              <p>
                Tasker Standard: ~$25–$30 at low volume. Drops to ~$18 at
                ~500/mo. They publish volume tiers in your dashboard once
                you've passed onboarding.
              </p>
            </div>
            <div>
              <p className="font-bold text-ink mb-1">What's the $25 credit back actually doing?</p>
              <p>
                A Stripe Connect transfer to the provider's payout account,
                fired by the booking-completion webhook after their first
                paid session settles. Code path:{" "}
                <code className="bg-cream px-1 rounded">routers/bookings.py</code>{" "}
                → <code>_credit_provider_background_check</code> (idempotent —
                tied to <code>background_check.credit_status</code> field).
              </p>
            </div>
            <div>
              <p className="font-bold text-ink mb-1">Can a provider re-run their check later (renewal)?</p>
              <p>
                Best practice: annual renewals. Not in the code yet — add a
                cron that flags providers with <code>completed_at</code>{" "}
                older than 365 days and re-charges $39 (no credit-back on
                renewals). Park as a P2 task in <code>ROADMAP.md</code>.
              </p>
            </div>
          </div>
        </section>

        {/* Quick links */}
        <section className="mt-10 pt-8 border-t border-[#EAEAEA]" data-testid="checkr-quick-links">
          <div className="flex items-center gap-2 mb-4">
            <Lightning size={18} weight="duotone" className="text-gold" />
            <h2 className="font-spectral text-xl text-ink">Quick links</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-3 text-[13.5px]">
            <a href="https://docs.checkr.com/" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between border border-[#E5E5E5] rounded-lg px-4 py-3 hover:border-gold hover:bg-cream/30 transition-colors">
              <span>Checkr API docs</span>
              <ArrowSquareOut size={14} className="text-gold-dark" />
            </a>
            <a href="https://dashboard.checkr.com/" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between border border-[#E5E5E5] rounded-lg px-4 py-3 hover:border-gold hover:bg-cream/30 transition-colors">
              <span>Checkr dashboard</span>
              <ArrowSquareOut size={14} className="text-gold-dark" />
            </a>
            <a href="https://docs.checkr.com/#tag/Webhooks" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between border border-[#E5E5E5] rounded-lg px-4 py-3 hover:border-gold hover:bg-cream/30 transition-colors">
              <span>Webhook reference</span>
              <ArrowSquareOut size={14} className="text-gold-dark" />
            </a>
            <a href="https://docs.checkr.com/#tag/Packages" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between border border-[#E5E5E5] rounded-lg px-4 py-3 hover:border-gold hover:bg-cream/30 transition-colors">
              <span>Package catalog</span>
              <ArrowSquareOut size={14} className="text-gold-dark" />
            </a>
          </div>
        </section>
      </div>
    </AppShell>
  );
}
